import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useAvailableRegions from './hooks/useAvailableRegions';
import useCreateRegistrar from './queries/useCreateRegistrar';

import FormBase from '@/components/forms/new/FormBase';
import { Alert, AlertDescription, LoadingIndicator } from 'components/v4';
import { useInitialRegion } from 'pages/resources/pbx/hooks/useInitialRegion';
import {
    breadCrumbTrail,
    ERROR_MESSAGES,
    addHeader as header,
    OWNERSHIP_MAPPING,
    PROTOCOL,
    registrarsPath,
} from './constants';
import PasswordField from './formFields/PasswordField';
import ProtocolField from './formFields/ProtocolField';
import ProxyField from './formFields/ProxyField';
import RegistrarNameField from './formFields/RegistrarNameField';
import RegistrarRegionField from './formFields/RegistrarRegionField';
import RegistrationDurationField from './formFields/RegistrationDurationField';
import URIField from './formFields/URIField';
import UsernameField from './formFields/UsernameField';

import appConfig from '@/config.json';

const RegistrarsAdd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { filter = 'Company' } = location.state || {};
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const { restError } = useSelector((state) => state.modal);
    const { currentCompany, currentPartner } = useSelector(
        (state) => state.navigationLists,
    );
    const [isDisabled, setIsDisabled] = useState(true);

    const statefulBreadCrumTrail = breadCrumbTrail.map(
        ({ label, path: prevPath }) => ({
            label,
            path: [prevPath, { index: prevPath ? 0 : null, filter }],
        }),
    );
    const isGlobalAdmin = requiredScope === appConfig.scope.global;

    const {
        data: regions = [],
        isLoading: regionsLoading,
        isFetching: regionsFetching,
        isRefetching: regionsRefetching,
        error: regionsError,
    } = useAvailableRegions();

    const { predictedInitialState } = useInitialRegion(regions, isGlobalAdmin);

    const handleSuccess = () => {
        toast.success('Registrar created successfully!');
        // Preserve URL search parameters when navigating back
        navigate(`${registrarsPath}${location.search}`, {
            state: { index: 0, filter },
        });
    };

    const handleError = (error) => {
        console.error('Failed to create registrar:', error);
        toast.error(
            restError ?? 'Failed to create registrar. Please try again.',
        );
    };

    const { mutate: createRegistrar, isPending } = useCreateRegistrar(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        // Only validate the 4 required fields
        if (!values.name || values.name.length < 2) {
            console.error(ERROR_MESSAGES.REGISTRAR_NAME_REQUIRED);
            return;
        }

        if (!values.regionID) {
            console.error(ERROR_MESSAGES.REGION_REQUIRED);
            return;
        }

        if (!values.uri) {
            console.error(ERROR_MESSAGES.URI_REQUIRED);
            return;
        }

        const isCompanyFilter = filter === 'Company';

        const finalSubmit = {
            name: values.name,
            uri: values.uri,
            protocol:
                values.protocol !== undefined
                    ? values.protocol
                    : PROTOCOL.DEFAULT, // Default to (0) if not set by user
            proxy: values.proxy || null, // proxy is nullable
            companyID: isCompanyFilter ? currentCompany : currentPartner,
            regionID: values.regionID,
            ownership: OWNERSHIP_MAPPING[filter] || 20,
            registrationDuration:
                parseInt(values.registrationDuration, 10) || 3600, // Default to 3600 seconds (1 hour)
            isPublic: isCompanyFilter ? false : true,
            username: values.username || '',
            password: values.password || '',
        };

        createRegistrar(finalSubmit);
    };

    const formFields = (errors, touched, values) => {
        // Only require name, regionID, uri
        const hasRequiredFields =
            values?.name && values?.regionID && values?.uri;

        if (hasRequiredFields) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        const fields = [
            <RegistrarNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <RegistrarRegionField
                key="regionID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                regions={regions}
                isDisabled={isDisabled}
            />,
            <URIField
                key="uri"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <ProxyField
                key="proxy"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <ProtocolField
                key="protocol"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <RegistrationDurationField
                key="registrationDuration"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <UsernameField
                key="username"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <PasswordField
                key="password"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
        ];

        return fields;
    };

    const isFetchingData =
        regionsLoading || regionsRefetching || regionsFetching || isPending;

    // Handle loading errors
    if (regionsError) {
        toast.error('Failed to load regions. Please refresh the page.');
    }

    if (isFetchingData) {
        return <LoadingIndicator />;
    }

    // Create partner alert component if filter is Partner and scope allows
    const partnerAlert =
        filter === 'Partner' && requiredScope > appConfig.scope.company ? (
            <Alert
                status="info"
                variant="inline-accent"
                sx={{ marginBottom: 3 }}>
                <AlertDescription>
                    You are creating a Registrar as a Partner. Registrars
                    defined at Partner level may be assigned to companies, but
                    not edited by company administrators.
                </AlertDescription>
            </Alert>
        ) : null;

    return (
        <FormBase
            isDisabled={isDisabled}
            breadCrumbTrail={statefulBreadCrumTrail}
            header={header}
            subHeader={filter === 'Company' ? null : ' '}
            subHeaderComponent={partnerAlert}
            initialValues={predictedInitialState}
            finalFormSubmit={finalFormSubmit}
            backLink={[
                `${registrarsPath}${location.search}`,
                { state: { index: 0, filter } },
            ]}>
            {({ errors, touched, values }) =>
                formFields(errors, touched, values)
            }
        </FormBase>
    );
};

export default RegistrarsAdd;
